import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';

export type AiLoadingPhase = 'enriching' | 'generating';

interface LoadingStage {
  label: string;
  state: 'done' | 'active' | 'pending';
}

const LOADING_HINTS: Record<AiLoadingPhase, string[]> = {
  enriching: [
    'Checking the entity knowledge base…',
    'Collecting identifiers and aliases…',
    'Reading company signals…',
  ],
  generating: [
    'Drafting search terms…',
    'Adding name variants and tickers…',
    'Filtering out the noise…',
    'Polishing the query…',
    'Almost there…',
  ],
};

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'app-ai-loading-card',
  templateUrl: './ai-loading-card.component.html',
  styleUrls: ['./ai-loading-card.component.scss'],
})
export class AiLoadingCardComponent implements OnChanges, OnDestroy {
  @Input() phase: AiLoadingPhase = 'generating';
  @Input() subject = '';
  @Input() showStages = false;
  @Output() cancelLoading = new EventEmitter<void>();

  private hintIndex = 0;
  // Recomputed only when inputs change (ngOnChanges), not per change-detection
  // pass — a getter here would hand *ngFor a new array identity every CD,
  // tearing down and rebuilding the stage DOM (and restarting the spinner).
  loadingStages: LoadingStage[] = [];
  private hintTimer: ReturnType<typeof setInterval> | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['phase'] || changes['showStages']) {
      this.loadingStages = this.computeStages();
    }
    if (changes['phase']) {
      this.startHints();
    }
  }

  ngOnDestroy(): void {
    this.stopHints();
  }

  get currentHint(): string {
    const hints = LOADING_HINTS[this.phase];
    return hints[Math.min(this.hintIndex, hints.length - 1)];
  }

  private computeStages(): LoadingStage[] {
    if (!this.showStages) return [];
    const generating = this.phase === 'generating';
    return [
      {
        label: 'Analyzing company profile',
        state: generating ? 'done' : 'active',
      },
      {
        label: 'Generating search parameters',
        state: generating ? 'active' : 'pending',
      },
    ];
  }

  private startHints(): void {
    this.stopHints();
    this.hintIndex = 0;
    // ngOnChanges clears and restarts this on every phase change, so reading
    // this.phase in the tick can never see a stale value.
    this.hintTimer = setInterval(() => {
      if (this.hintIndex >= LOADING_HINTS[this.phase].length - 1) {
        this.stopHints();
        return;
      }
      this.hintIndex++;
    }, 3200);
  }

  private stopHints(): void {
    if (this.hintTimer !== null) {
      clearInterval(this.hintTimer);
      this.hintTimer = null;
    }
  }
}
